import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X, Download, FileText, ImageIcon, FileSpreadsheet, FileType2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const MAX_BYTES = 10 * 1024 * 1024;

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fileIcon(mime) {
  if (mime?.startsWith("image/")) return ImageIcon;
  if (mime?.includes("pdf")) return FileType2;
  if (mime?.includes("sheet") || mime?.includes("excel") || mime?.includes("csv")) return FileSpreadsheet;
  return FileText;
}

function buildDownloadUrl(att) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const token = document.cookie.split("; ").find((c) => c.startsWith("session_token="))?.split("=")[1];
  return `${base}/api/attachments/${att.attachment_id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
}

export default function CardAttachments({ cardId }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["attachments", cardId],
    queryFn: async () => (await api.get(`/cards/${cardId}/attachments`)).data,
    enabled: !!cardId,
  });

  const attachments = data?.attachments || [];

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name} pesa más de 10 MB`);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/cards/${cardId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      qc.invalidateQueries({ queryKey: ["attachments", cardId] });
      qc.invalidateQueries({ queryKey: ["activity"] });
      toast.success(`${file.name} adjuntado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
    }
  };

  const uploadMany = async (files) => {
    for (const f of files) {
      await upload(f);
    }
  };

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/attachments/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attachments", cardId] });
      toast.success("Adjunto eliminado");
    },
    onError: () => toast.error("No se pudo eliminar"),
  });

  return (
    <section data-testid="card-attachments-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
          <Paperclip className="h-3 w-3" /> Adjuntos {attachments.length > 0 && <span>({attachments.length})</span>}
        </h4>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-xs h-7"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="attach-button"
        >
          {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Paperclip className="h-3 w-3 mr-1" />}
          Adjuntar
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const list = Array.from(e.target.files || []);
            if (list.length) uploadMany(list);
            e.target.value = "";
          }}
          data-testid="attach-file-input"
        />
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const list = Array.from(e.dataTransfer.files || []);
          if (list.length) uploadMany(list);
        }}
        className={cn(
          "rounded-lg border border-dashed text-center py-3 text-xs transition-colors",
          isDragging
            ? "border-blue-400 bg-blue-50/60 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
            : "border-white/40 dark:border-white/10 bg-white/30 dark:bg-slate-900/20 text-muted-foreground"
        )}
        data-testid="attach-dropzone"
      >
        {isDragging ? "Suelta para subir" : "Arrastra archivos aquí · máx 10 MB c/u"}
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-xs text-muted-foreground mt-2">Cargando…</p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="attachments-list">
          {attachments.map((a) => (
            <AttachmentRow
              key={a.attachment_id}
              attachment={a}
              onRemove={() => remove.mutate(a.attachment_id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function AttachmentRow({ attachment, onRemove }) {
  const Icon = fileIcon(attachment.mime_type);
  const url = buildDownloadUrl(attachment);
  return (
    <li
      className="flex items-center gap-3 p-2 rounded-xl bg-white/60 dark:bg-slate-900/40 border border-white/40 dark:border-white/10 hover:shadow-sm transition"
      data-testid={`attachment-${attachment.attachment_id}`}
    >
      {attachment.is_image ? (
        <a href={url} target="_blank" rel="noreferrer" className="h-12 w-12 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
          <img
            src={url}
            alt={attachment.filename}
            className="h-full w-full object-cover"
            loading="lazy"
            data-testid={`attachment-preview-${attachment.attachment_id}`}
          />
        </a>
      ) : (
        <a href={url} target="_blank" rel="noreferrer" className="h-12 w-12 rounded-lg grid place-items-center bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-300 shrink-0">
          <Icon className="h-5 w-5" />
        </a>
      )}
      <div className="min-w-0 flex-1">
        <a
          href={url} target="_blank" rel="noreferrer"
          className="text-sm font-medium truncate block hover:text-blue-600 dark:hover:text-blue-300"
          data-testid={`attachment-name-${attachment.attachment_id}`}
        >
          {attachment.filename}
        </a>
        <p className="text-[11px] text-muted-foreground">
          {humanSize(attachment.size)} · {(attachment.mime_type || "archivo").split("/").pop()}
        </p>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="p-1.5 rounded-md hover:bg-white/60 dark:hover:bg-white/10 text-muted-foreground hover:text-foreground"
        title="Descargar"
        data-testid={`attachment-download-${attachment.attachment_id}`}
      >
        <Download className="h-4 w-4" />
      </a>
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 rounded-md hover:bg-rose-500/10 text-muted-foreground hover:text-rose-600"
        title="Eliminar"
        data-testid={`attachment-delete-${attachment.attachment_id}`}
      >
        <X className="h-4 w-4" />
      </button>
    </li>
  );
}
